import type { BimWorkOrderOtherPriceData } from 'gql/graphql';
import { BimContractOtherStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';

interface IUseBimWorkOrderOtherTableRowMenuItemsAvailability {
    statusWorkOrder?: WorkOrderStatusEnum;
    dataRow?: BimWorkOrderOtherPriceData;
    isFrameContract: boolean;
}

enum BimWorkOrderOtherOperations {
    ChangeDistribution = 'ChangeDistribution',
    Edit = 'Edit',
    View = 'View',
    Delete = 'Delete',
}

const completedAndDelayedAvailability = {
    [BimContractOtherStatusEnum.ContractCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
    [BimContractOtherStatusEnum.AdditionalCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
    [BimContractOtherStatusEnum.NewRecord]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
};

const getInProgressAvailabilityMap = (hasNoAcceptedCosts: boolean, hasActualCost: boolean) => ({
    [BimContractOtherStatusEnum.ContractCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: hasNoAcceptedCosts,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: !hasActualCost,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
    [BimContractOtherStatusEnum.AdditionalCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: hasNoAcceptedCosts,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: !hasActualCost,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
    [BimContractOtherStatusEnum.NewRecord]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: false,
            [BimWorkOrderOtherOperations.Edit]: hasNoAcceptedCosts,
            [BimWorkOrderOtherOperations.View]: !hasNoAcceptedCosts,
            [BimWorkOrderOtherOperations.Delete]: hasNoAcceptedCosts,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: !hasActualCost,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: false,
        },
    },
});

const getOnFormationAvailabilityMap = (isFrameContract: boolean) => ({
    [BimContractOtherStatusEnum.ContractCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
    },
    [BimContractOtherStatusEnum.AdditionalCondition]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: false,
            [BimWorkOrderOtherOperations.View]: true,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
    },
    [BimContractOtherStatusEnum.NewRecord]: {
        isActKC2Price: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: isFrameContract,
            [BimWorkOrderOtherOperations.View]: !isFrameContract,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
        isWorkOrderPrice: {
            [BimWorkOrderOtherOperations.ChangeDistribution]: true,
            [BimWorkOrderOtherOperations.Edit]: isFrameContract,
            [BimWorkOrderOtherOperations.View]: !isFrameContract,
            [BimWorkOrderOtherOperations.Delete]: true,
        },
    },
});

const getMap = (hasNoAcceptedCosts: boolean, hasActualCost: boolean, isFrameContract: boolean) => ({
    [WorkOrderStatusEnum.OnFormation]: getOnFormationAvailabilityMap(isFrameContract),
    [WorkOrderStatusEnum.InProgress]: getInProgressAvailabilityMap(hasNoAcceptedCosts, hasActualCost),
    [WorkOrderStatusEnum.Completed]: completedAndDelayedAvailability,
    [WorkOrderStatusEnum.Delayed]: completedAndDelayedAvailability,
});

export const getBimWorkOrderOtherTableRowMenuItemsAvailability = (
    props: IUseBimWorkOrderOtherTableRowMenuItemsAvailability,
) => {
    const { statusWorkOrder = WorkOrderStatusEnum.OnFormation, dataRow, isFrameContract } = props;

    const idCostPlan = dataRow?.actKC2OtherCosts?.[0]?.actKC2Id;
    const keyValue = R.pick([`actsKc2OtherCostPlan${idCostPlan}`], dataRow);
    const isZero = replacerCost(Object.values(keyValue)[0] as any) === 0;
    const emptyValueOrZero = !!dataRow?.hasEmptyActPlanCost || isZero;
    const otherStatus = dataRow?.status ?? BimContractOtherStatusEnum.NewRecord;
    const hasNoAcceptedCosts = emptyValueOrZero && !!dataRow?.hasEmptyActAcceptedCost && !!dataRow?.hasEmptyActPlanCost;
    const { hasActualCost } = dataRow ?? {};

    const isActKC2Price = !!dataRow?.isActKC2Price;
    const isActKc2Key = isActKC2Price ? 'isActKC2Price' : 'isWorkOrderPrice';

    const map = getMap(hasNoAcceptedCosts, Boolean(hasActualCost), isFrameContract);

    return map[statusWorkOrder][otherStatus][isActKc2Key];
};
